import { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Link2, X, RotateCcw, Upload } from "lucide-react";
import {
    LinkCandidate,
    PlacePoolStatus,
    PlaceSourceKey,
    PlaceSourcesStatus,
    PooledPlace,
    dismissPlace,
    getPlaceSourcesStatus,
    getUnmatchedPlaces,
    linkPlace,
    restorePlace,
    searchLinkCandidates,
} from "../../../interfaces/placeSources";

// /bo/unmatched-places — places OpenStreetMap / DATAtourisme list that no establishment could be
// linked to. Link one by hand (approved straight onto the establishment, like a reviewed Google
// match) or dismiss it. Backend: /admin/place-sources (crm-back services/placeSources).

const SOURCE_LABEL: Record<PlaceSourceKey, string> = { osm: "OpenStreetMap", datatourisme: "DATAtourisme" };
const STATUS_LABEL: Record<PlacePoolStatus, string> = { unmatched: "À relier", linked: "Reliés", dismissed: "Écartés" };
const fmt = (n?: number) => (n ?? 0).toLocaleString("fr-FR");
const when = (d?: string) => (d ? new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—");

function SourceCard({ source, status }: { source: PlaceSourceKey; status?: PlaceSourcesStatus["sources"][PlaceSourceKey] }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl p-3 flex flex-col gap-1 text-xs">
            <span className="text-sm font-semibold text-gray-900">{SOURCE_LABEL[source]}</span>
            <span className="text-gray-500">Dernier import : {when(status?.last?.at)}</span>
            <span className="text-gray-500">
                Pool : {fmt(status?.pool.unmatched)} à relier · {fmt(status?.pool.linked)} reliés à la main · {fmt(status?.pool.dismissed)} écartés
            </span>
        </div>
    );
}

function LinkPanel({ place, onDone, onClose }: { place: PooledPlace; onDone: () => void; onClose: () => void }) {
    const [q, setQ] = useState(place.name ?? "");
    const [postcode, setPostcode] = useState(place.postcode ?? "");
    const [items, setItems] = useState<LinkCandidate[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const search = () => {
        setBusy(true);
        setError(null);
        searchLinkCandidates(q, postcode || undefined)
            .then(setItems)
            .catch(() => setError("Recherche impossible."))
            .finally(() => setBusy(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(search, []);

    const link = (est: LinkCandidate) => {
        setBusy(true);
        linkPlace(place._id, est._id)
            .then(onDone)
            .catch((e) => setError(e?.response?.data?.error ?? "Liaison impossible."))
            .finally(() => setBusy(false));
    };

    return (
        <tr className="bg-[#F9F6F3]">
            <td colSpan={6} className="px-4 py-3">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, enseigne ou SIRET" className="rounded-sm border border-gray-200 px-2 py-1 text-xs w-64" />
                    <input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Code postal" className="rounded-sm border border-gray-200 px-2 py-1 text-xs w-28" />
                    <button onClick={search} disabled={busy} className="rounded-sm border border-gray-200 bg-white px-2 py-1 text-xs">Chercher</button>
                    <button onClick={onClose} className="ml-auto text-xs text-gray-500 hover:text-gray-700">Fermer</button>
                </div>
                {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
                {items.length === 0 && !busy && <p className="text-xs text-gray-500">Aucun établissement trouvé.</p>}
                <div className="flex flex-col gap-1">
                    {items.map((est) => (
                        <div key={est._id} className="flex items-center justify-between gap-2 bg-white border border-gray-200 rounded-sm px-2 py-1.5 text-xs">
                            <span className="text-gray-800">
                                <b>{est.name}</b>
                                {est.registration?.enseigne && est.registration.enseigne !== est.name && ` (${est.registration.enseigne})`} · {est.address?.street}{" "}
                                {est.address?.postcode} {est.address?.city} · SIRET {est.registration?.siret ?? "—"} · {est.registration?.naf_code ?? ""}
                            </span>
                            <button onClick={() => link(est)} disabled={busy} className="rounded-sm bg-black text-white px-2 py-1 shrink-0">Relier et approuver</button>
                        </div>
                    ))}
                </div>
            </td>
        </tr>
    );
}

export default function UnmatchedPlaces() {
    const [status, setStatus] = useState<PlaceSourcesStatus | null>(null);
    const [source, setSource] = useState<PlaceSourceKey | "">("");
    const [poolStatus, setPoolStatus] = useState<PlacePoolStatus>("unmatched");
    const [q, setQ] = useState("");
    const [postcode, setPostcode] = useState("");
    const [page, setPage] = useState(1);
    const [data, setData] = useState<{ items: PooledPlace[]; total: number; limit: number } | null>(null);
    const [loading, setLoading] = useState(true);
    const [linking, setLinking] = useState<string | null>(null);
    const requestRef = useRef(0);

    const loadStatus = () => getPlaceSourcesStatus().then(setStatus).catch(() => {});
    const load = () => {
        const id = ++requestRef.current;
        setLoading(true);
        getUnmatchedPlaces({ source, status: poolStatus, q: q || undefined, postcode: postcode || undefined, page, limit: 50 })
            .then((d) => id === requestRef.current && setData(d))
            .catch(() => {})
            .finally(() => id === requestRef.current && setLoading(false));
    };

    useEffect(() => {
        loadStatus();
    }, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(load, [source, poolStatus, page]);

    const act = (fn: (id: string) => Promise<any>, id: string) => fn(id).then(() => { load(); loadStatus(); });
    const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-6 py-6 flex flex-col gap-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Lieux non reliés</h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Lieux d'OpenStreetMap et de DATAtourisme qu'aucun établissement n'a pu reprendre automatiquement. Reliez-les à la main ou écartez-les.
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/bo/import-lieux" className="flex items-center gap-1.5 rounded-sm bg-black text-white px-3 py-1.5 text-xs">
                            <Upload className="w-3.5 h-3.5" /> Importer les fichiers
                        </Link>
                        <button onClick={() => { load(); loadStatus(); }} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
                            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualiser
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(["osm", "datatourisme"] as PlaceSourceKey[]).map((s) => (
                        <SourceCard key={s} source={s} status={status?.sources?.[s]} />
                    ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex rounded-sm border border-gray-200 bg-white overflow-hidden">
                        {(Object.keys(STATUS_LABEL) as PlacePoolStatus[]).map((s) => (
                            <button key={s} onClick={() => { setPoolStatus(s); setPage(1); }} className={`px-3 py-1.5 text-xs ${poolStatus === s ? "bg-black text-white" : "text-gray-600 hover:bg-gray-50"}`}>
                                {STATUS_LABEL[s]}
                            </button>
                        ))}
                    </div>
                    <select value={source} onChange={(e) => { setSource(e.target.value as any); setPage(1); }} className="rounded-sm border border-gray-200 bg-white px-2 py-1.5 text-xs">
                        <option value="">Toutes les sources</option>
                        <option value="osm">OpenStreetMap</option>
                        <option value="datatourisme">DATAtourisme</option>
                    </select>
                    <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())} placeholder="Nom ou ville" className="rounded-sm border border-gray-200 bg-white px-2 py-1.5 text-xs w-48" />
                    <input value={postcode} onChange={(e) => setPostcode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())} placeholder="Code postal" className="rounded-sm border border-gray-200 bg-white px-2 py-1.5 text-xs w-28" />
                    <button onClick={() => { setPage(1); load(); }} className="rounded-sm border border-gray-200 bg-white px-2 py-1.5 text-xs">Filtrer</button>
                    {data && <span className="text-xs text-gray-500 ml-auto">{fmt(data.total)} lieux</span>}
                </div>

                <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead>
                            <tr className="text-gray-500">
                                <th className="text-left font-medium px-4 py-2">Lieu</th>
                                <th className="text-left font-medium px-2 py-2">Adresse</th>
                                <th className="text-left font-medium px-2 py-2">Contacts</th>
                                <th className="text-left font-medium px-2 py-2">Source</th>
                                <th className="text-left font-medium px-2 py-2">Vu le</th>
                                <th className="px-4 py-2" />
                            </tr>
                        </thead>
                        <tbody>
                            {data?.items.map((p) => (
                                <Fragment key={p._id}>
                                    <tr className="border-t border-gray-100 align-top">
                                        <td className="px-4 py-2">
                                            <span className="font-medium text-gray-800">{p.data.name ?? "—"}</span>
                                            <span className="block text-gray-400">{p.data.category?.replace(/^amenity=|^shop=|^tourism=|^datatourisme:/, "")}{p.data.cuisine?.length ? ` · ${p.data.cuisine.join(", ")}` : ""}</span>
                                        </td>
                                        <td className="px-2 py-2 text-gray-600">{[p.data.street, [p.data.postcode, p.data.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "—"}</td>
                                        <td className="px-2 py-2 text-gray-600 break-all">
                                            {[p.data.phone, p.data.website, p.data.email].filter(Boolean).join(" · ") || "—"}
                                        </td>
                                        <td className="px-2 py-2 text-gray-500 whitespace-nowrap">
                                            {p.source === "osm" ? (
                                                <a className="underline" href={`https://www.openstreetmap.org/${p.sourceId}`} target="_blank" rel="noreferrer">OSM</a>
                                            ) : (
                                                <a className="underline" href={p.sourceId} target="_blank" rel="noreferrer">DATAtourisme</a>
                                            )}
                                            {!p.active && <span className="block text-gray-400">plus listé</span>}
                                        </td>
                                        <td className="px-2 py-2 text-gray-500 whitespace-nowrap">{when(p.lastSeen)}</td>
                                        <td className="px-4 py-2 text-right whitespace-nowrap">
                                            {p.status === "unmatched" && (
                                                <>
                                                    <button onClick={() => setLinking(linking === p._id ? null : p._id)} className="inline-flex items-center gap-1 rounded-sm border border-gray-200 px-2 py-1 mr-1 hover:bg-gray-50">
                                                        <Link2 className="w-3 h-3" /> Relier
                                                    </button>
                                                    <button onClick={() => act(dismissPlace, p._id)} className="inline-flex items-center gap-1 rounded-sm border border-gray-200 px-2 py-1 hover:bg-gray-50">
                                                        <X className="w-3 h-3" /> Écarter
                                                    </button>
                                                </>
                                            )}
                                            {p.status === "dismissed" && (
                                                <button onClick={() => act(restorePlace, p._id)} className="inline-flex items-center gap-1 rounded-sm border border-gray-200 px-2 py-1 hover:bg-gray-50">
                                                    <RotateCcw className="w-3 h-3" /> Remettre
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                    {linking === p._id && (
                                        <LinkPanel
                                            place={p}
                                            onClose={() => setLinking(null)}
                                            onDone={() => {
                                                setLinking(null);
                                                load();
                                                loadStatus();
                                            }}
                                        />
                                    )}
                                </Fragment>
                            ))}
                            {data && data.items.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-6 text-center text-gray-500">Aucun lieu.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {pages > 1 && (
                    <div className="flex items-center justify-end gap-2 text-xs">
                        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-sm border border-gray-200 bg-white px-2 py-1 disabled:opacity-40">Précédent</button>
                        <span className="text-gray-500">Page {page} / {pages}</span>
                        <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded-sm border border-gray-200 bg-white px-2 py-1 disabled:opacity-40">Suivant</button>
                    </div>
                )}

                <p className="text-xs text-gray-400">Données OpenStreetMap © les contributeurs OpenStreetMap (ODbL) · DATAtourisme sous Licence Ouverte 2.0.</p>
            </div>
        </div>
    );
}
